import React, { useState, useEffect } from 'react';
import { Schema, Settings } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
// import { generateBreadcrumbPath } from './common/breadcrumbs'; // Commented out - breadcrumbs not needed
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Tooltip,
} from '@mui/material';
import FlowParameterDrawer from './MasterFlowDrawer/FlowParameterDrawer';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow`;
const CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;

const MasterFlow = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [flows, setFlows] = useState([]);
  const [connectorOptions, setConnectorOptions] = useState([]);
  const [isParameterDrawerOpen, setIsParameterDrawerOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [selectedConnector, setSelectedConnector] = useState(null);

  // Check if user is CDX user
  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };

  // Add useEffect to fetch data on component mount
  useEffect(() => {
    const loadData = async () => {
      await fetchConnectorOptions();
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (connectorOptions.length > 0) {
      fetchFlows();
    }
  }, [connectorOptions]);

  const fetchConnectorOptions = async () => {
    try {
      console.log('Fetching connector options...'); // Debug log
      const response = await fetch(CONNECTOR_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Connector options response:', data); // Debug log

      // Store the full connector objects for later use
      setConnectorOptions(data);
    } catch (error) {
      console.error('Error fetching connector options:', error);
      toast.error(`Error loading connector options: ${error.message}`);
    }
  };

  const fetchFlows = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching flows...'); // Debug log
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response:', data); // Debug log

      // In the fetchFlows function in MasterFlow.js
      const mappedData = data.map(item => {
        // Find source and target connector names
        const sourceConnector = connectorOptions.find(c => c.mcId === item.mfnSourceAppId);
        const targetConnector = connectorOptions.find(c => c.mcId === item.mfnTargetAppId);
        
        return {
          ...item,
          // Add an id field that the DataGrid requires - use mfRecId or fallback to a UUID
          id: item.mfId || item.mfId || `temp-${Math.random().toString(36).substring(2, 11)}`,
          // Ensure mfRecId is always populated
          mfRecId: item.mfRecId,
          mfId: item.mfId || '',
          // Add source and target connector names for display
          sourceConnectorName: sourceConnector ? sourceConnector.mcName : 'Not Selected',
          targetConnectorName: targetConnector ? targetConnector.mcName : 'Not Selected'
        };
      });

      console.log('Mapped data for grid:', mappedData);
      setFlows(mappedData);

      return mappedData;
    } catch (error) {
      console.error('Error details:', error); // Debug log
      toast.error(`Error fetching flows: ${error.message}`);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      const formattedData = buildPayload(data);

      // Remove empty fields or null values that might cause validation issues
      // But preserve mfHeaderCode even if empty (to allow clearing)
      const cleanData = Object.fromEntries(
        Object.entries(formattedData).filter(([key, v]) => {
          if (key === 'mfHeaderCode') return true;
          return v !== null && v !== '';
        })
      );

      console.log('Creating flow with clean data:', cleanData);

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cleanData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Create response:', responseData);
      toast.success('Flow created successfully');
      await fetchFlows();
      return responseData;
    } catch (error) {
      console.error('Create error full details:', error);
      toast.error(`Error creating flow: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const formattedData = buildPayload(data);
      // API path expects master flow UUID (mfId), not rec_id
      const flowId = data.mfId || formattedData.mfId || id;
      
      console.log('Updating flow with formatted data:', formattedData);
      
      const response = await fetch(`${API_BASE_URL}/${flowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formattedData)
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
  
      const responseData = await response.json();
      console.log('Update response:', responseData);
      toast.success('Flow updated successfully');
      await fetchFlows();
      return responseData;
    } catch (error) {
      console.error('Update error full details:', error);
      toast.error(`Error updating flow: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      console.log('Deleting flow:', id);
  
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      toast.success('Flow deleted successfully');
      await fetchFlows();
      return true;
    } catch (error) {
      console.error('Delete error full details:', error);
      toast.error(`Error deleting flow: ${error.message}`);
      throw error;
    }
  };

  const openParameterDrawer = (flowId) => {
    console.log('Opening parameter drawer for flow ID:', flowId);
    const flow = flows.find(f => f.mfId === flowId);
    if (flow) {
      setSelectedFlow(flow);
      
      // Find connector if needed
      if (connectorOptions.length > 0) {
        // Choose appropriate connector based on your business logic
        const sourceConnector = connectorOptions.find(c => c.mcId === flow.mfnSourceAppId);
        setSelectedConnector(sourceConnector || connectorOptions[0]);
      }
      
      setIsParameterDrawerOpen(true);
    } else {
      console.error('Flow not found with ID:', flowId);
      toast.error('Error: Flow not found');
    }
  };

  const columns = [
    { field: 'mfName', headerName: 'Name', width: 500 },
    { field: 'mfFlowFunction', headerName: 'Flow Function', width: 350 },
    { field: 'mfHeaderCode', headerName: 'Header Code', width: 200 },
    { field: 'sourceConnectorName', headerName: 'Source Application', width: 180 },
    { field: 'targetConnectorName', headerName: 'Target Application', width: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Tooltip title="Manage Parameters">
            <Button
              variant="outlined"
              size="small"
              startIcon={<Settings />}
              onClick={(e) => {
                e.stopPropagation();
                openParameterDrawer(params.row.mfId);
              }}
            >
              Params
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Create dropdown options for connectors - ensure we're using UUIDs as string values
  const connectorDropdownOptions = connectorOptions.map(conn => ({
    id: conn.mcId,
    value: conn.mcId, // Use the UUID directly as a string
    label: conn.mcName
  }));

  const formFields = [
    {
      name: 'mfName',
      label: 'Name',
      required: true,
      validate: (value) => {
        if (!value) return 'Name is required';
        if (value.length < 3) return 'Name must be at least 3 characters';
        return null;
      }
    },
    {
      name: 'mfFlowFunction',
      label: 'Flow Function',
      required: true,
      validate: (value) => !value ? 'Flow Function is required' : null
    },
    {
      name: 'mfHeaderCode',
      label: 'Header Code',
      required: false,
      validate: (value) => {
        if (value && value.length > 100) return 'Header Code must be 100 characters or less';
        return null;
      }
    },
    {
      name: 'mfnSourceAppId',
      label: 'Source Application',
      type: 'select',
      options: connectorDropdownOptions,
      renderOption: (option) => option.label,
      getOptionLabel: (option) => option.label,
      getOptionValue: (option) => option.value,
      isOptionEqualToValue: (option, value) => option.value === value,
      validate: (value) => !value ? 'Source Application is required' : null,
      required: true
    },
    {
      name: 'mfnTargetAppId',
      label: 'Target Application',
      type: 'select',
      options: connectorDropdownOptions,
      renderOption: (option) => option.label,
      getOptionLabel: (option) => option.label,
      getOptionValue: (option) => option.value,
      isOptionEqualToValue: (option, value) => option.value === value,
      validate: (value) => !value ? 'Target Application is required' : null,
      required: true
    },
  ];

  const initialFormData = {
    mfRecId: '',
    mfId: '',
    mfName: '',
    mfFlowFunction: '',
    mfHeaderCode: '',
    mfnSourceAppId: '',
    mfnTargetAppId: '',
  };

  // const breadcrumbPaths = generateBreadcrumbPath('masterflow'); // Commented out - breadcrumbs not needed

  // Custom function to prepare data for edit form
  const prepareEditData = (row) => {
    console.log('Original row data for edit:', row); // Debug log
    
    // Make sure we're passing the actual UUID strings for the dropdown fields
    const preparedData = {
      ...row,
      mfnSourceAppId: row.mfnSourceAppId || '',
      mfnTargetAppId: row.mfnTargetAppId || '',
    };
    
    console.log('Prepared data for edit form:', preparedData);
    return preparedData;
  };

  const buildPayload = (data) => {
    const formattedData = {
      ...data,
      mfnSourceAppId: data.mfnSourceAppId || null,
      mfnTargetAppId: data.mfnTargetAppId || null,
      mfHeaderCode: data.mfHeaderCode || null,
    };
    delete formattedData.uiConfig;
    delete formattedData.mfScreenUi;
    delete formattedData.sourceConnectorName;
    delete formattedData.targetConnectorName;
    delete formattedData.id;
    return formattedData;
  };

  return (
    <>
      <DynamicContainer
        title="Master Flow"
        icon={Schema}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="mfRecId"
        // breadcrumbPaths={breadcrumbPaths} // Commented out - breadcrumbs not needed
        rows={flows}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        prepareEditData={prepareEditData}
        isLoading={isLoading}
        valueField="value" // Add this to specify the field containing the dropdown value
        labelField="label" // Add this to specify the field containing the dropdown label
        isCdxUser={isCdxUser()}
        functionCode="Master Flows"
      />

      {/* Flow Parameter Drawer */}
      <FlowParameterDrawer
        open={isParameterDrawerOpen}
        onClose={() => setIsParameterDrawerOpen(false)}
        flowId={selectedFlow?.mfId}
        flowName={selectedFlow?.mfName}
        mcId={selectedConnector?.mcId}
        mfId={selectedFlow?.mfId}
      />
    </>
  );
};

export default MasterFlow;